import { type LucideIcon, Repeat } from "lucide-react";
import { lazy } from "react";
import type { ReactElement } from "react";

import type { FeatureName } from "@/lib/features";

/**
 * The report catalog — one entry per report.
 *
 * Reports used to be a single page: `/modules/reports` *was* the Sales Pipeline
 * report, with its card and its generate button inline. That left nowhere to
 * put a second one. Now `/modules/reports` is a catalog index and each report
 * gets its own route and page, so adding one is an entry here plus a page
 * component — no edits to the index, the sidebar, or the router.
 *
 * Reports are deliberately their own registry rather than plain
 * `AppRoute`s in `reports-app.tsx`: the index page needs to *enumerate* them
 * with descriptions to render its card grid, which a flat route list can't
 * express. `reports-app.tsx` derives its nav and routes from this.
 */
export interface ReportDefinition {
  /** URL slug under `/modules/reports/`. */
  slug: string;
  /** Catalog card + sidebar label. */
  label: string;
  /** One-line explanation of what the report contains. */
  description: string;
  icon: LucideIcon;
  element: ReactElement;
  /**
   * Optional gates, same semantics as `AppRoute`. A gated report disappears
   * from both the catalog grid and the sidebar, and its route bounces.
   */
  requiredPermission?: string;
  requiredFeature?: FeatureName;
  requiresSuperAdmin?: boolean;
  /** Stays open in Demo Mode — see `AppRoute.demoSafe`. */
  demoSafe?: boolean;
}

/**
 * Sales Pipeline (AB#7875) is the legacy workbook again, but it is run from
 * the Energy Advisor dashboard's Pipeline Report button, not from here; its
 * rebuild as a standard spreadsheet report is withdrawn (`withdrawn` on its
 * definition in `pilot-mod-reports`' `report_builder/standard`). The Catalog
 * sidebar group and landing section appear only when the registry holds a
 * report.
 */
const AutoRenewalReportPage = lazy(
  () => import("./auto-renewal/AutoRenewalReportPage"),
);

export const REPORTS: ReportDefinition[] = [
  {
    slug: "auto-renewal",
    label: "Auto-Renewal",
    description:
      "Every C&I renewal deal the auto-renewal process acted on — the matrix offer or incumbent RFQ it started, when, and whether it went out.",
    icon: Repeat,
    element: <AutoRenewalReportPage />,
    requiredPermission: "reports.view",
    requiredFeature: "auto_renewal",
  },
];

/**
 * Full path for a report, for links and route registration.
 *
 * Nested under `/reports`, the Reports module's catalog.
 */
export function reportPath(slug: string): string {
  return `/reports/${slug}`;
}

export const REPORTS_INDEX_PATH = "/reports";
